import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "zone", "preview", "previewThumb", "previewName", "previewSize", "error", "progress", "progressBar", "submit"]
  static values = { accept: String, maxSize: Number, autoSubmit: { type: Boolean, default: false } }

  browse() {
    this.inputTarget.click()
  }

  dragOver(e) {
    e.preventDefault()
    this.zoneTarget.style.borderColor = "var(--bs-primary)"
    this.zoneTarget.style.background = "rgba(124, 58, 237, 0.04)"
  }

  dragLeave(e) {
    e.preventDefault()
    this.zoneTarget.style.borderColor = ""
    this.zoneTarget.style.background = ""
  }

  drop(e) {
    e.preventDefault()
    this.zoneTarget.style.borderColor = ""
    this.zoneTarget.style.background = ""
    const file = e.dataTransfer.files[0]
    if (file) this.handleFile(file)
  }

  fileSelected(e) {
    const file = e.target.files[0]
    if (file) this.handleFile(file)
  }

  handleFile(file) {
    this.hideError()

    if (this.hasAcceptValue && this.acceptValue) {
      const allowed = this.acceptValue.split(",").map(t => t.trim())
      const match = allowed.some(t => {
        if (t.endsWith("/*")) return file.type.startsWith(t.replace("/*", "/"))
        return file.type === t
      })
      if (!match) {
        this.showError("This file type is not supported.")
        return
      }
    }

    if (this.hasMaxSizeValue && this.maxSizeValue > 0 && file.size > this.maxSizeValue) {
      this.showError(`File must be less than ${this.formatSize(this.maxSizeValue)}.`)
      return
    }

    // Set file on hidden input for form submission
    const dt = new DataTransfer()
    dt.items.add(file)
    this.inputTarget.files = dt.files

    // Show preview
    this.zoneTarget.classList.add("d-none")
    this.previewTarget.classList.remove("d-none")
    this.previewNameTarget.textContent = file.name
    this.previewSizeTarget.textContent = this.formatSize(file.size)

    this.previewThumbTarget.innerHTML = ""
    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.src = URL.createObjectURL(file)
      img.style.cssText = "width:48px;height:48px;object-fit:cover;border-radius:8px;"
      this.previewThumbTarget.appendChild(img)
    } else {
      const ext = file.name.split(".").pop().toUpperCase().slice(0, 4)
      this.previewThumbTarget.innerHTML = `<span class="fw-bold text-muted" style="font-size:0.6875rem;">${ext}</span>`
    }

    // Start direct upload immediately
    this.startDirectUpload(file)
  }

  startDirectUpload(file) {
    const url = this.inputTarget.dataset.directUploadUrl || "/rails/active_storage/direct_uploads"

    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("d-none")
      this.progressBarTarget.style.width = "0%"
    }
    this.setSubmitDisabled(true)

    const upload = new DirectUpload(file, url, this)
    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        if (this.hasProgressTarget) this.progressTarget.classList.add("d-none")
        this.setSubmitDisabled(false)
        return
      }

      // Replace the file input with a hidden signed_id field
      const hiddenField = document.createElement("input")
      hiddenField.type = "hidden"
      hiddenField.name = this.inputTarget.name
      hiddenField.value = blob.signed_id
      this.inputTarget.insertAdjacentElement("afterend", hiddenField)
      // Clear the file input so we don't double-upload on form submit
      this.inputTarget.disabled = true

      if (this.hasProgressTarget) {
        this.progressBarTarget.style.width = "100%"
        this.progressBarTarget.classList.remove("progress-bar-animated")
        this.progressBarTarget.classList.add("bg-success")
      }
      this.previewSizeTarget.textContent = `${this.formatSize(file.size)} · Uploaded`
      this.setSubmitDisabled(false)

      if (this.autoSubmitValue) {
        const form = this.element.closest("form")
        if (form) {
          this.previewSizeTarget.textContent = `${this.formatSize(file.size)} · Saving...`
          setTimeout(() => form.requestSubmit(), 300)
        }
      }
    })
  }

  // DirectUpload delegate: progress callback
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => {
      if (this.hasProgressBarTarget && event.lengthComputable) {
        const pct = Math.round((event.loaded / event.total) * 100)
        this.progressBarTarget.style.width = `${pct}%`
      }
    })
  }

  clear() {
    this.inputTarget.value = ""
    this.inputTarget.disabled = false
    // Remove any hidden signed_id field we added
    const hidden = this.inputTarget.nextElementSibling
    if (hidden && hidden.type === "hidden" && hidden.name === this.inputTarget.name) {
      hidden.remove()
    }
    this.zoneTarget.classList.remove("d-none")
    this.previewTarget.classList.add("d-none")
    this.previewThumbTarget.innerHTML = ""
    if (this.hasProgressTarget) this.progressTarget.classList.add("d-none")
    this.hideError()
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = disabled
    } else {
      // Fall back to finding submit button in the closest form
      const form = this.element.closest("form")
      const btn = form?.querySelector("[type='submit']")
      if (btn) btn.disabled = disabled
    }
  }

  showError(msg) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = msg
      this.errorTarget.classList.remove("d-none")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("d-none")
    }
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
